/**
 * useEcosystemDataset — single React hook that fetches the live
 * ecosystem feeds, runs the adapters, caches last-known-good in
 * sessionStorage, and falls back to safe local scaffolds if the API
 * is unreachable.
 *
 * Returns the EXACT shape the JTR roadmap/changelog UI kit expects:
 *   { MILESTONES, QUARTERS, STATUS_META, CATEGORY_META, RELEASES,
 *     LAUNCH_DATE, ROADMAP_VERSION,
 *     loaded, error, stale, source }
 *
 * Data priority order (per owner directive):
 *   1. Live API with publicDisplayApproved=true
 *   2. Cached last-known-good API data (sessionStorage, 1h)
 *   3. Safe local fallback scaffold from /data/roadmapData +
 *      /data/changelogReleases
 *   4. Empty state
 */
import { useEffect, useMemo, useState } from "react";

import { fetchJsonDeduped } from "@/lib/dedupedFetch";
import {
  STATUS_META as ADAPTER_STATUS_META,
  SERVICE_CATEGORY_META,
  mapEcosystemRoadmap,
  mapEcosystemChangelog,
} from "@/lib/ecosystemAdapter";

import {
  MILESTONES as FALLBACK_MILESTONES,
  QUARTERS as FALLBACK_QUARTERS,
  CATEGORY_META as FALLBACK_CATEGORY_META,
  LAUNCH_DATE,
  ROADMAP_VERSION,
} from "@/data/roadmapData";

import { RELEASES as FALLBACK_RELEASES } from "@/data/changelogReleases";

import {
  NOJO_ROADMAP_ITEMS,
  NOJO_CHANGELOG_ITEMS,
  NOJO_ROADMAP_QUARTERS,
} from "@/data/nojoCorporateUpdates";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CACHE_TTL_MS = 60 * 60 * 1000; // 1h

function readCache(key) {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { ts, payload } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    return payload;
  } catch {
    return null;
  }
}

function writeCache(key, payload) {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), payload }));
  } catch {
    // quota / private mode — silent
  }
}

async function fetchFeed(path, lang) {
  // Pull the full Mongo-backed history in one call so client-side
  // filter/pagination keeps working. Backend pageSize cap is 200 — we
  // request that to get everything currently stored. Once one of our
  // partners crosses 200 items we'll switch to true server-side pagination.
  const url = `${BACKEND}/api/integrations/ecosystem/${path}?lang=${lang}&pageSize=200&page=1`;
  const data = await fetchJsonDeduped(url);
  if (!data || !Array.isArray(data.items)) throw new Error("API shape unexpected");
  return data;
}

export function useEcosystemDataset(lang = "en") {
  const [state, setState] = useState({
    roadmapApi: null,
    changelogApi: null,
    loaded: false,
    error: null,
    source: "loading",
  });
  useEffect(() => {
    let aborted = false;
    const cacheKeyRoadmap = `nojo:ecosystem:roadmap:${lang}`;
    const cacheKeyChangelog = `nojo:ecosystem:changelog:${lang}`;

    // 1) Optimistically render cached payloads first so the page never
    //    flashes empty when the user revisits.
    const cachedRoadmap = readCache(cacheKeyRoadmap);
    const cachedChangelog = readCache(cacheKeyChangelog);
    if (cachedRoadmap || cachedChangelog) {
      setState({
        roadmapApi: cachedRoadmap,
        changelogApi: cachedChangelog,
        loaded: true,
        error: null,
        source: "cache",
      });
    }

    // 2) Always refresh in the background.
    Promise.all([
      fetchFeed("roadmap", lang).catch((e) => ({ error: e })),
      fetchFeed("changelog", lang).catch((e) => ({ error: e })),
    ]).then(([r, c]) => {
      if (aborted) return;
      const roadmapOk = r && !r.error;
      const changelogOk = c && !c.error;
      if (roadmapOk) writeCache(cacheKeyRoadmap, r);
      if (changelogOk) writeCache(cacheKeyChangelog, c);
      setState({
        roadmapApi: roadmapOk ? r : cachedRoadmap || null,
        changelogApi: changelogOk ? c : cachedChangelog || null,
        loaded: true,
        error: !roadmapOk && !changelogOk ? "all-failed" : null,
        source: roadmapOk || changelogOk ? "live" : cachedRoadmap || cachedChangelog ? "cache" : "fallback",
      });
    });

    return () => {
      aborted = true;
    };
  }, [lang]);

  // Run adapters on each render — they're pure and cheap.
  const dataset = useMemo(() => {
    const { roadmapApi, changelogApi, loaded, error, source } = state;

    let MILESTONES = FALLBACK_MILESTONES;
    let QUARTERS = FALLBACK_QUARTERS;
    let CATEGORY_META = FALLBACK_CATEGORY_META;
    let RELEASES = FALLBACK_RELEASES;
    let effectiveSource = source;
    let stale = false;

    if (roadmapApi) {
      const mapped = mapEcosystemRoadmap(roadmapApi, lang);
      if (mapped.milestones.length > 0) {
        MILESTONES = mapped.milestones;
        QUARTERS = mapped.quarters;
        CATEGORY_META = { ...SERVICE_CATEGORY_META, ...mapped.categoryMeta };
      } else {
        effectiveSource = "fallback";
      }
      const fr = roadmapApi.sourceFreshness;
      if (Array.isArray(fr) && fr.some((s) => s && s.stale === true)) stale = true;
      if (roadmapApi.stale) stale = true;
    } else if (loaded) {
      effectiveSource = "fallback";
    }

    // Merge in NoJo Enterprise corporate-site roadmap items so the
    // unified Roadmap page surfaces the parent company alongside
    // JEC and JTR as a third filterable source.
    if (NOJO_ROADMAP_ITEMS.length > 0) {
      MILESTONES = [...MILESTONES, ...NOJO_ROADMAP_ITEMS];
      const existingKeys = new Set(QUARTERS.map((q) => q.key));
      NOJO_ROADMAP_QUARTERS.forEach((qKey) => {
        if (!existingKeys.has(qKey)) {
          const m = qKey.match(/q(\d)_(\d{4})/);
          const label = m
            ? { en: `Q${m[1]} ${m[2]}`, ja: `${m[2]}年第${m[1]}四半期` }
            : { en: qKey, ja: qKey };
          QUARTERS = [...QUARTERS, { key: qKey, label }];
          existingKeys.add(qKey);
        }
      });
      QUARTERS = [...QUARTERS].sort((a, b) => {
        if (a.key === "unscheduled") return 1;
        if (b.key === "unscheduled") return -1;
        const ma = a.key.match(/q(\d)_(\d{4})/);
        const mb = b.key.match(/q(\d)_(\d{4})/);
        if (!ma || !mb) return a.key.localeCompare(b.key);
        const av = parseInt(ma[2], 10) * 10 + parseInt(ma[1], 10);
        const bv = parseInt(mb[2], 10) * 10 + parseInt(mb[1], 10);
        return bv - av;
      });
      CATEGORY_META = {
        ...CATEGORY_META,
        "nojo-enterprise": SERVICE_CATEGORY_META["nojo-enterprise"],
      };
    }

    if (changelogApi) {
      const mapped = mapEcosystemChangelog(changelogApi, lang);
      if (mapped.length > 0) RELEASES = mapped;
      const fr = changelogApi.sourceFreshness;
      if (Array.isArray(fr) && fr.some((s) => s && s.stale === true)) stale = true;
      if (changelogApi.stale) stale = true;
    }

    // Merge NoJo Enterprise corporate-site releases into What's New
    // so the page is the single home for all NoJo ecosystem updates.
    // Sorted newest-first by sortDate; `latest` flag is recomputed
    // so the most recent release overall wins the LATEST badge.
    if (NOJO_CHANGELOG_ITEMS.length > 0) {
      const combined = [...RELEASES, ...NOJO_CHANGELOG_ITEMS].sort((a, b) => {
        const ad = new Date(a.sortDate || a.date || 0).getTime();
        const bd = new Date(b.sortDate || b.date || 0).getTime();
        return bd - ad;
      });
      RELEASES = combined.map((r, i) => ({ ...r, latest: i === 0 }));
    }

    return {
      MILESTONES,
      QUARTERS,
      STATUS_META: ADAPTER_STATUS_META,
      CATEGORY_META,
      RELEASES,
      LAUNCH_DATE,
      ROADMAP_VERSION,
      loaded,
      error,
      stale,
      source: effectiveSource,
      // Per-service freshness — merged from both feeds. Each entry:
      // { serviceId, lastSuccessfulSyncAtJst, stale }
      sourceFreshness: (() => {
        const map = {};
        const merge = (api) => {
          const arr = api && api.sourceFreshness;
          if (!Array.isArray(arr)) return;
          arr.forEach((s) => {
            if (!s || !s.serviceId) return;
            const prev = map[s.serviceId];
            // Keep the newest timestamp if both feeds report it.
            if (!prev || (s.lastSuccessfulSyncAtJst || "") > (prev.lastSuccessfulSyncAtJst || "")) {
              map[s.serviceId] = s;
            }
          });
        };
        merge(roadmapApi);
        merge(changelogApi);
        return Object.values(map);
      })(),
    };
  }, [state, lang]);

  return dataset;
}
